import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as creditNoteActions from '@redux/modules/creditNote'
import type { DeleteParams, ModuleState } from '@redux/modules/types'

import type { AppDispatch } from '@redux/store'

import PageContext from '@contexts/pageContext'

import defaultFormState, { requiredFields } from '@models/creditNote'

import type { CreditNoteModel, CreditNoteRequestOptions } from '@models/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (creditNote?: Partial<CreditNoteModel>, options?: GenerateUrlsOptions) => {
  const { id } = creditNote || {}

  const { prefixRoute = 'systemManager' } = options || {}
  const isSystemManager = prefixRoute === 'systemManager'

  return {
    editCreditNoteDetailsUrl: `#/${prefixRoute}/creditNotes/${id}/edit/details`,
    editCreditNoteUrl: isSystemManager ? `#/${prefixRoute}/creditNotes/${id}/edit/`
      : `#/${prefixRoute}/edit/accounting`,
    creditNotesIndexUrl: `#/${prefixRoute}/creditNotes`,
  }
}

type CreateCreditNoteParams = {
  creditNoteParams: Partial<CreditNoteModel>,
  dispatch: AppDispatch,
  requestOptions?: CreditNoteRequestOptions,
}

const createCreditNote = (params: CreateCreditNoteParams) => {
  const { dispatch, creditNoteParams, requestOptions } = params
  const { createCreditNote: createFn } = creditNoteActions

  return dispatch(createFn(creditNoteParams, requestOptions))
}

type CreateCreditNoteOnAccountingApiParams = {
  creditNote: CreditNoteModel,
  dispatch: AppDispatch,
  requestOptions?: CreditNoteRequestOptions,
}

const createExternalCreditNote = (params: CreateCreditNoteOnAccountingApiParams) => {
  const { creditNote, dispatch, requestOptions } = params

  const { createExternalCreditNote: createFn } = creditNoteActions
  return dispatch(createFn(creditNote, requestOptions))
}

type UpdateCreditNoteParams = {
  creditNote: CreditNoteModel,
  creditNoteParams: Partial<CreditNoteModel>,
  dispatch: AppDispatch,
  requestOptions?: CreditNoteRequestOptions,
}

const updateCreditNote = (params: UpdateCreditNoteParams) => {
  const {
    dispatch, creditNote, creditNoteParams, requestOptions,
  } = params
  const { updateCreditNote: updateFn } = creditNoteActions

  const updatedParams = {
    id: creditNote.id,
    ...creditNoteParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCreditNoteParams = {
  creditNote: DeleteParams<CreditNoteModel>,
  dispatch: AppDispatch,
}

const deleteCreditNote = (params: DeleteCreditNoteParams) => {
  const { dispatch, creditNote } = params
  const { deleteCreditNote: deleteFn } = creditNoteActions

  return dispatch(deleteFn(creditNote))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCreditNoteForm(
  creditNote: Partial<CreditNoteModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const creditNoteForm = useForm(
    defaultFormState,
    { entity: creditNote, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [creditNote.id, creditNote.cache_key],
  )

  return {
    ...creditNoteForm,
  }
}

export function useRelations(creditNote: Partial<CreditNoteModel> = {}) {
  const { campaign_id, creditor_type, creditor_id } = creditNote

  const {
    agencies, brands, providers, campaigns,
  } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2538
  const agency = creditor_type === 'Agency' && agencies[creditor_id]
  // @ts-expect-error TS2538
  const brand = creditor_type === 'Brand' && brands[creditor_id]
  // @ts-expect-error TS2538
  const provider = creditor_type === 'Provider' && providers[creditor_id]

  const campaign = campaign_id ? campaigns[campaign_id] || {} : {}

  return {
    campaign,
    creditor: agency || brand || provider || {},
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CreditNoteRequestOptions,
}

function useCreditNote(initEntity: Partial<CreditNoteModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: creditNote }: { entity: CreditNoteModel} = useLatestEntity(initEntity, 'creditNotes')
  const { id } = creditNote

  const dispatch = useDispatch()

  const {
    creditor, campaign,
  } = useRelations(creditNote)

  useReduxAction(
    'creditNotes',
    'loadCreditNote',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.creditNotes)

  const { callbacks } = useContext(PageContext)

  return {
    callbacks: {
      createCreditNote: (creditNoteParams: Partial<CreditNoteModel>, entityOptions?: CreditNoteRequestOptions) => (
        createCreditNote({ creditNoteParams, dispatch, requestOptions: entityOptions })
      ),
      createExternalCreditNote: (entityOptions?: CreditNoteRequestOptions) => (
        createExternalCreditNote({
          creditNote,
          dispatch,
          requestOptions: entityOptions,
        })
      ),
      createOrEditCreditNote: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCreditNoteModal',
        payload: { creditNote },
      }),
      deleteCreditNote: () => deleteCreditNote({ dispatch, creditNote }),
      updateCreditNote: (creditNoteParams: Partial<CreditNoteModel>, entityOptions?: CreditNoteRequestOptions) => (
        updateCreditNote({
          creditNote, creditNoteParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    creditor,
    creating,
    creditNote,
    deleting,
    loading: loading && !loadedIds.includes(creditNote.id),
    updating,
    urls: generateUrls(creditNote),
  }
}

export type UseCreditNotePayload = ReturnType<typeof useCreditNote>

export default useCreditNote
